import { Injectable } from '@angular/core';
import { AclService } from '@y42/acl';
import {
  AclOrgPermission,
  AclOrgSpacePermission,
  AclOrgSpaceSettings,
  AclPermission,
  AclPermissionDetail,
  AclPermissionsRecord,
  AclResource,
  AclResourceType,
  AclRole,
  AclRoleAssignment,
  AclSelectRolesContext,
  AclStrategy,
  AclSubject,
} from '@y42/acl/models';
import { AclQuery } from '@y42/acl/state';
import { ErrorHandlerService } from '@y42/error-handling';
import { createAclUserSubject } from '@y42/org-user/acl';
import { OrgQuery } from '@y42/org/state';
import { UserQuery } from '@y42/user/state';
import { isNullish } from '@y42/util/nullish';
import { map, Observable, of } from 'rxjs';
import { OrgSpaceStoreModel } from '../models';
import { OrgSpaceQuery } from '../state';

@Injectable({ providedIn: 'root' })
export class OrgSpaceAclService {
  constructor(
    private readonly aclService: AclService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly aclQuery: AclQuery,
    private readonly orgQuery: OrgQuery,
    private readonly orgSpaceQuery: OrgSpaceQuery,
    private readonly userQuery: UserQuery,
  ) {}

  enumerateOrgSpaces({
    subject,
    permissions,
  }: {
    subject?: AclSubject;
    permissions: AclPermission[];
  }): Observable<AclResource[]> {
    return this.aclService.enumerateResources({
      resourceType: AclResourceType.Space,
      subject,
      permissions,
    });
  }

  getOrgSpaceSettings({ spaceId }: { spaceId: OrgSpaceStoreModel['id'] }): Observable<AclOrgSpaceSettings> {
    return this.aclService.getSpaceSettings({ spaceId });
  }

  setOrgSpaceSettings({
    spaceId,
    settings,
  }: {
    spaceId: OrgSpaceStoreModel['id'];
    settings: AclOrgSpaceSettings;
  }): Observable<boolean> {
    return this.aclService.setSpaceSettings({ spaceId, settings });
  }

  getOrgSpaceRoleTooltip({ role }: { role: AclRole }): string {
    return this.aclQuery.getRoleTooltip({ role, resourceType: AclResourceType.Space });
  }

  getOrgSpaceRoles(): AclRole[] {
    return this.aclQuery.getRoles({ resourceType: AclResourceType.Space });
  }

  convertOrgSpaceRoleToGrantPermission(role: AclRole): AclOrgSpacePermission {
    return this.aclService.convertRoleToGrantPermission(role) as AclOrgSpacePermission;
  }

  getOrgSpacePermission({
    spaceId,
    permission,
  }: {
    spaceId?: OrgSpaceStoreModel['id'];
    permission: AclOrgSpacePermission;
    strategy?: AclStrategy;
  }): AclPermissionDetail | null {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (!spaceId) {
      this.errorHandlerService.error('[OrgSpaceAclService.getOrgSpacePermission]: nullish spaceId');
      return { allowed: false };
    }

    return this.aclQuery.getPermission({
      resourceType: AclResourceType.Space,
      resourceId: spaceId,
      permission,
    });
  }

  checkOrgSpacePermission({
    spaceId,
    permission,
    strategy,
  }: {
    spaceId?: OrgSpaceStoreModel['id'];
    permission: AclOrgSpacePermission;
    strategy?: AclStrategy;
  }): Observable<AclPermissionDetail> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (!spaceId) {
      this.errorHandlerService.error('[OrgSpaceAclService.checkOrgSpacePermission]: nullish spaceId');
      return of({ allowed: false });
    }

    return this.aclService.checkPermission({
      resourceType: AclResourceType.Space,
      resourceId: spaceId,
      permission,
      strategy,
    });
  }

  checkOrgSpacePermissions({
    spaceId,
    permissions,
    strategy,
  }: {
    spaceId?: OrgSpaceStoreModel['id'];
    permissions: AclOrgSpacePermission[];
    strategy?: AclStrategy;
  }): Observable<AclPermissionsRecord> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (!spaceId) {
      this.errorHandlerService.error('[OrgSpaceAclService.checkOrgSpacePermissions]: nullish spaceId');
      return of({} as AclPermissionsRecord);
    }

    return this.aclService.checkPermissions({
      resourceType: AclResourceType.Space,
      resourceId: spaceId,
      permissions,
      strategy,
    });
  }

  checkCanCreateOrgSpaces({
    orgId,
    strategy,
  }: { orgId?: number; strategy?: AclStrategy } = {}): Observable<AclPermissionDetail> {
    orgId = orgId ?? this.orgQuery.getActive()?.id;

    if (isNullish(orgId)) {
      this.errorHandlerService.error(`[OrgSpaceAclService.checkCanCreateOrgSpaces]: nullish orgId`);
      return of({ allowed: false });
    }

    return this.aclService.checkPermission({
      resourceType: AclResourceType.Org,
      resourceId: `${orgId}`,
      permission: AclOrgPermission.CreateSpaces,
      strategy,
    });
  }

  filterOrgSpaceRolesBasedOnGrantedPermissions({
    roles,
    spaceId,
    strategy,
  }: {
    roles: AclRole[];
    spaceId?: OrgSpaceStoreModel['id'];
    strategy?: AclStrategy;
  }): Observable<AclRole[]> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (!spaceId) {
      this.errorHandlerService.error(
        '[OrgSpaceAclService.filterOrgSpaceRolesBasedOnGrantedPermissions]: nullish spaceId',
      );
      return of([]);
    }

    return this.aclService.filterRolesBasedOnGrantedPermissions({
      roles,
      resourceType: AclResourceType.Space,
      resourceId: spaceId,
      strategy,
    });
  }

  getOrgSpaceRoleAssignments({
    spaceId,
    subject,
    strategy,
  }: {
    spaceId?: OrgSpaceStoreModel['id'];
    subject?: AclSubject;
    strategy?: AclStrategy;
  } = {}): Observable<AclRoleAssignment[]> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (!spaceId) {
      this.errorHandlerService.error('[OrgSpaceAclService.getOrgSpaceRoleAssignments]: nullish spaceId');
      return of([]);
    }

    return this.aclService
      .getRoleAssignments({
        resourceType: AclResourceType.Space,
        resourceId: spaceId,
        spaceId,
        strategy,
        subject,
      })
      .pipe(
        this.errorHandlerService.catchError(
          '[OrgSpaceAclService.getOrgSpaceRoleAssignments]: could not fetch role assignments',
          () => of([]),
          {
            toast: `Could not fetch role assignments for space "${spaceId}"`,
            context: { spaceId },
          },
        ),
      );
  }

  getOrgSpaceRoleForActiveUser(): Observable<AclRole | null> {
    const activeUser = this.userQuery.getActiveUser();
    if (!activeUser) {
      return of(null);
    }

    const subject = createAclUserSubject(activeUser.id);
    return this.getOrgSpaceRoleAssignments({ subject }).pipe(map((assignments) => assignments.at(0)?.role || null));
  }

  setOrgSpaceRole({
    role,
    spaceId,
    subject,
  }: {
    role: AclRole;
    spaceId?: OrgSpaceStoreModel['id'];
    subject: AclSubject;
  }): Observable<boolean> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (!spaceId) {
      this.errorHandlerService.error('[OrgSpaceAclService.setOrgSpaceRole]: nullish spaceId');
      return of(false);
    }

    return this.aclService
      .setRole({
        resourceType: AclResourceType.Space,
        resourceId: spaceId,
        spaceId,
        subject,
        role,
      })
      .pipe(
        this.errorHandlerService.catchError(
          '[OrgSpaceAclService.setOrgSpaceRole]: could not set role for space',
          () => of(false),
          {
            toast: `Could not set the role '${role}' for the space '${spaceId}'`,
            context: { role, spaceId },
          },
        ),
      );
  }

  revokeOrgSpaceRole({
    spaceId,
    subject,
  }: {
    spaceId?: OrgSpaceStoreModel['id'];
    subject: AclSubject;
  }): Observable<boolean> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (!spaceId) {
      this.errorHandlerService.error('[OrgSpaceAclService.revokeOrgSpaceRole]: nullish spaceId');
      return of(false);
    }

    return this.aclService
      .revokeRole({
        resourceType: AclResourceType.Space,
        resourceId: spaceId,
        spaceId,
        subject,
      })
      .pipe(
        this.errorHandlerService.catchError(
          '[OrgSpaceAclService.revokeOrgSpaceRole]: could not revoke role for space',
          () => of(false),
          {
            toast: `Could not revoke the role for the space '${spaceId}'`,
            context: { spaceId },
          },
        ),
      );
  }

  selectOrgSpaceRoleAssignmentsBySpaceId({
    spaceId,
  }: {
    spaceId: OrgSpaceStoreModel['id'];
  }): Observable<AclRoleAssignment[]> {
    return this.aclQuery.selectRoleAssignments({
      resourceType: AclResourceType.Space,
      resourceId: spaceId,
    });
  }

  getOrgSpaceRolesContextForRoleSelect({
    spaceId,
    subject,
    strategy,
  }: {
    spaceId: OrgSpaceStoreModel['id'];
    subject?: AclSubject;
    strategy?: AclStrategy;
  }): Observable<AclSelectRolesContext> {
    return this.aclService.getRolesContextForRoleSelect({
      resourceId: spaceId,
      resourceType: AclResourceType.Space,
      subject,
      strategy,
    });
  }

  resetAllOrgSpaceRoleAssignments(): void {
    this.aclService.resetAllRoleAssignments({
      resourceTypes: [AclResourceType.Space],
    });
  }
}
